import React, { useState } from 'react';
import { 
  setUserPointsDirectly, 
  addUserPointsDirectly
} from '../../utils/pointsSystem';
import { db } from '../../firebase';
import { collection, query, where, getDocs, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { Coins, ShieldAlert, Sparkles, UserCheck, AlertCircle } from 'lucide-react';
import './UserPointsManager.css';

const CONTEMPT_PENALTIES = [
  'Clown of the Docket 🤡',
  'Dunce Badge 🎪',
  'Subpoenaed 📜',
  'Guilty of Slander ⚖️',
  'Silenced in Court 🤐',
  'Contempt Citation 🔨'
];

const UserPointsManager = () => {
  const [userIdentifier, setUserIdentifier] = useState('');
  const [points, setPoints] = useState(100);
  const [pointsDelta, setPointsDelta] = useState(50);
  const [selectedPenalty, setSelectedPenalty] = useState(CONTEMPT_PENALTIES[0]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info'); // info, success, error
  const [loading, setLoading] = useState(false);

  const resolveUserId = async (idOrEmail) => {
    if (!idOrEmail) throw new Error('User ID or Email is required');
    const trimmed = idOrEmail.trim();
    if (trimmed.includes('@')) {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', trimmed));
      const snap = await getDocs(q);
      if (snap.empty) throw new Error('No juror found with email: ' + trimmed);
      return snap.docs[0].id;
    }
    return trimmed;
  };

  const showFeedback = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 6000);
  };

  const handleSetPoints = async () => {
    if (!userIdentifier.trim()) {
      showFeedback('Juror ID or email is required', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await setUserPointsDirectly(userIdentifier.trim(), Number(points));
      if (res.success) {
        showFeedback(`Court Chips set to ${points} for juror ${userIdentifier}`, 'success');
      } else {
        showFeedback(res.error || 'Failed to set chips', 'error');
      }
    } catch (err) {
      showFeedback(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPoints = async (isDeduct = false) => {
    if (!userIdentifier.trim()) {
      showFeedback('Juror ID or email is required', 'error');
      return;
    }
    setLoading(true);
    try {
      const delta = isDeduct ? -Math.abs(Number(pointsDelta)) : Math.abs(Number(pointsDelta));
      const res = await addUserPointsDirectly(userIdentifier.trim(), delta);
      if (res.success) {
        showFeedback(`${delta > 0 ? 'Granted' : 'Deducted'} ${Math.abs(delta)} Court Chips. New balance: ${res.newTotal}`, 'success');
      } else {
        showFeedback(res.error || 'Failed to adjust chips', 'error');
      }
    } catch (err) {
      showFeedback(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleIssuePenalty = async () => {
    if (!userIdentifier.trim()) {
      showFeedback('Juror ID or email is required', 'error');
      return;
    }
    setLoading(true);
    try {
      const uid = await resolveUserId(userIdentifier);
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        contemptBadges: arrayUnion(selectedPenalty)
      });
      showFeedback(`Slapped "${selectedPenalty}" penalty on juror record!`, 'success');
    } catch (err) {
      showFeedback(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePardonJuror = async () => {
    if (!userIdentifier.trim()) {
      showFeedback('Juror ID or email is required', 'error');
      return;
    }
    setLoading(true);
    try {
      const uid = await resolveUserId(userIdentifier);
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        contemptBadges: []
      });
      showFeedback(`Juror record pardoned! All contempt citations removed.`, 'success');
    } catch (err) {
      showFeedback(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="juror-sanctions-manager">
      <div className="manager-header">
        <h2>
          <Coins className="header-icon" />
          Juror Chips & Sanctions Ledger
        </h2>
        <p>Adjust Court Chips bounties, hand down Contempt citations, or pardon reformed jurors.</p>
      </div>

      <div className="sanction-card-section">
        <label className="field-label">Target Juror (User ID or Email):</label>
        <input
          type="text"
          value={userIdentifier}
          onChange={(e) => setUserIdentifier(e.target.value)}
          placeholder="e.g. friend@example.com or Firebase UID"
          className="sanction-input full-width"
        />
        <span className="field-hint">Enter your friend's login email address or their Firestore UID.</span>
      </div>

      <div className="sanction-grid">
        {/* Chips Management */}
        <div className="sanction-card-section">
          <h3>
            <Sparkles size={18} className="sec-icon" />
            Court Chips Allocation
          </h3>
          
          <div className="action-row">
            <div className="input-with-label">
              <span className="sub-label">Set Absolute Chips:</span>
              <input
                type="number"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                min="0"
                className="sanction-input"
              />
            </div>
            <button 
              onClick={handleSetPoints} 
              disabled={loading}
              className="btn-sanction btn-primary"
            >
              Set Total
            </button>
          </div>

          <div className="action-row">
            <div className="input-with-label">
              <span className="sub-label">Adjust Balance:</span>
              <input
                type="number"
                value={pointsDelta}
                onChange={(e) => setPointsDelta(e.target.value)}
                min="1"
                className="sanction-input"
              />
            </div>
            <div className="btn-group">
              <button 
                onClick={() => handleAddPoints(false)} 
                disabled={loading}
                className="btn-sanction btn-grant"
              >
                + Grant
              </button>
              <button 
                onClick={() => handleAddPoints(true)} 
                disabled={loading}
                className="btn-sanction btn-deduct"
              >
                - Deduct
              </button>
            </div>
          </div>
        </div>

        {/* Contempt & Sanctions */}
        <div className="sanction-card-section">
          <h3>
            <ShieldAlert size={18} className="sec-icon danger" />
            Contempt & Penalties
          </h3>

          <div className="action-row">
            <div className="input-with-label">
              <span className="sub-label">Select Contempt Badge:</span>
              <select
                value={selectedPenalty}
                onChange={(e) => setSelectedPenalty(e.target.value)}
                className="sanction-select"
              >
                {CONTEMPT_PENALTIES.map((p, idx) => (
                  <option key={idx} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <button 
              onClick={handleIssuePenalty} 
              disabled={loading}
              className="btn-sanction btn-penalty"
            >
              Issue Penalty
            </button>
          </div>

          <div className="pardon-section">
            <p className="pardon-text">Grant total clemency to clear all active contempt tags from this juror's profile.</p>
            <button 
              onClick={handlePardonJuror} 
              disabled={loading}
              className="btn-sanction btn-pardon"
            >
              <UserCheck size={16} /> Pardon & Clear Record
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div className={`sanction-alert alert-${messageType}`}>
          <AlertCircle size={18} />
          <span>{message}</span>
        </div>
      )}
    </div>
  );
};

export default UserPointsManager;